import * as RadixTabs from '@radix-ui/react-tabs';
import { memo, type ReactNode, useCallback, useEffect, useMemo } from 'react';

import { useTrackAssetModalCommentPanelClose, useTrackAssetModalInfoPanelClose } from '@air/analytics';
import { ArrowRightToLineIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { useAssetModalRightPanel } from '~/components/AssetModalV2/AssetModalRightPanelContextProvider';
import { InfoPanelAssetMetadata } from '~/components/AssetModalV2/InfoPanel/components/InfoPanelAssetMetadata';
import { InfoPanelTabsContent } from '~/components/AssetModalV2/InfoPanel/components/InfoPanelTabsContent';
import { isValidAssetModalRightPanelsTab } from '~/components/AssetModalV2/utils/isValidAssetModalRightPanelsTab';
import { type PanelTab, PanelTabsList } from '~/components/v2/components/shared/panel/PanelTabsList';

export type InfoPanelProps = {
  canEdit: boolean;
  canViewInfo?: boolean;
  canViewVersions: boolean;
  canViewComments: boolean;
  versionChip?: ReactNode;
  infoView: ReactNode;
  commentsView: ReactNode;
  insightsView?: ReactNode;
};

type InfoPanelTab = PanelTab & {
  tooltip: string;
};

const INFO_PANEL_COMMENTS_TAB: InfoPanelTab = {
  id: 'comments',
  label: 'Comments',
  value: 'comments',
  tooltip: 'Comments',
};

const INFO_PANEL_INSIGHTS_TAB: InfoPanelTab = {
  id: 'insights',
  label: 'Insights',
  value: 'insights',
  tooltip: 'Insights',
};

const INFO_PANEL_INFO_TAB: InfoPanelTab = {
  id: 'info',
  label: 'Info',
  value: 'info',
  tooltip: 'Info',
};

export const InfoPanel = memo(
  ({
    canEdit,
    canViewInfo = true,
    canViewVersions: _canViewVersions,
    canViewComments,
    versionChip,
    infoView,
    commentsView,
    insightsView,
  }: InfoPanelProps) => {
    const { activeRightPanelTab, isRightPanelOpen, isRightPanelTabOpen, showRightPanelTab, closeRightPanel } =
      useAssetModalRightPanel();
    const { trackAssetModalInfoPanelClose } = useTrackAssetModalInfoPanelClose();
    const { trackAssetModalCommentPanelClose } = useTrackAssetModalCommentPanelClose();

    const onClose = useCallback(() => {
      if (isRightPanelTabOpen('info')) {
        trackAssetModalInfoPanelClose();
      } else if (isRightPanelOpen) {
        trackAssetModalCommentPanelClose();
      }
      closeRightPanel();
    }, [
      isRightPanelTabOpen,
      closeRightPanel,
      isRightPanelOpen,
      trackAssetModalInfoPanelClose,
      trackAssetModalCommentPanelClose,
    ]);

    const onValueChange = useCallback(
      (value: string) => {
        if (isValidAssetModalRightPanelsTab(value)) {
          showRightPanelTab(value);
        }
      },
      [showRightPanelTab],
    );

    const infoPanelTabs = useMemo(() => {
      const tabs: InfoPanelTab[] = [];

      if (canViewInfo) {
        tabs.push(INFO_PANEL_INFO_TAB);
      }

      if (canViewComments) {
        tabs.push(INFO_PANEL_COMMENTS_TAB);
      }

      if (insightsView) {
        tabs.push(INFO_PANEL_INSIGHTS_TAB);
      }

      return tabs;
    }, [canViewComments, canViewInfo, insightsView]);

    const activeTab = useMemo(() => {
      if (!isRightPanelOpen) {
        return null;
      }

      if (infoPanelTabs.some((tab) => tab.value === activeRightPanelTab)) {
        return activeRightPanelTab;
      }

      if (isRightPanelTabOpen('info') && !canViewInfo) {
        return infoPanelTabs[0]?.value;
      }

      return activeRightPanelTab;
    }, [activeRightPanelTab, canViewInfo, infoPanelTabs, isRightPanelOpen, isRightPanelTabOpen]);

    useEffect(() => {
      if (
        isRightPanelTabOpen('info') &&
        activeTab &&
        activeTab !== activeRightPanelTab &&
        isValidAssetModalRightPanelsTab(activeTab)
      ) {
        showRightPanelTab(activeTab);
      }
    }, [activeRightPanelTab, activeTab, isRightPanelTabOpen, showRightPanelTab]);

    if (!activeTab) {
      return null;
    }

    return (
      <RadixTabs.Root asChild defaultValue={activeTab} onValueChange={onValueChange} value={activeTab}>
        <div className="flex h-full flex-col gap-2 ads:text-metadata text-gray-10" data-testid="INFO_PANEL">
          <div className="flex flex-col gap-2 p-4">
            <div className="flex items-center justify-between">
              <PanelTabsList tabs={infoPanelTabs} triggerTestId="INFO_PANEL_TABS_LIST_TRIGGER" />

              <IconButton
                adsIcon
                icon={ArrowRightToLineIcon}
                label="Hide Right Panel"
                appearance="ghost"
                size="medium"
                color="gray"
                onClick={onClose}
              />
            </div>
            <InfoPanelAssetMetadata canEdit={canEdit} versionChip={versionChip} />
          </div>

          <div className="relative min-h-0 flex-1 overflow-hidden">
            {canViewInfo && (
              <InfoPanelTabsContent side="left" value="info">
                {infoView}
              </InfoPanelTabsContent>
            )}
            {canViewComments && (
              <InfoPanelTabsContent side="right" value="comments">
                {commentsView}
              </InfoPanelTabsContent>
            )}
            {insightsView && (
              <InfoPanelTabsContent side="right" value="insights">
                {insightsView}
              </InfoPanelTabsContent>
            )}
          </div>
        </div>
      </RadixTabs.Root>
    );
  },
);

InfoPanel.displayName = 'InfoPanel';
